import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import { User, Clock, X, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { FunctionalConsentGate } from "@/components/cookie-consent/FunctionalConsentGate";
import { intlLocaleFor } from "@/lib/appLocale";

export type MarkerStatus = "red" | "yellow" | "green" | "orange" | "gray" | "hidden";

export type AdminMapMode = "cleaning" | "snow";

export interface MapLocation {
  id: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  status: MarkerStatus;
  /** Current worker (from task_execution_logs CHECK_IN); null if none */
  currentWorkerName?: string | null;
  /** Next pending task name or "Brak zadań" */
  nextTaskName?: string | null;
  /** Task progress from calculateTaskProgress (ujednolicone z PropertyCard) */
  taskProgress?: { total: number; completed: number } | null;
  /** Last snow session completed after the selected cutoff */
  lastCompletedAt?: string | null;
}

interface AdminMapProps {
  locations: MapLocation[];
  /** Selected location ID for Quick View panel; null = panel hidden */
  selectedLocationId?: string | null;
  onMarkerClick?: (id: string) => void;
  onClosePreview?: () => void;
  onDetails?: (id: string) => void;
  className?: string;
  mode?: AdminMapMode;
}

const defaultCenter: [number, number] = [52.2297, 21.0122];

const OSM_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION = "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a>";

const MARKER_RADIUS = 10;
const MARKER_WEIGHT = 2;

const statusStyles: Record<
  MarkerStatus,
  { fillColor: string; color: string }
> = {
  red: { fillColor: "#ef4444", color: "#ffffff" },
  yellow: { fillColor: "#eab308", color: "#ffffff" },
  green: { fillColor: "#22c55e", color: "#ffffff" },
  orange: { fillColor: "#f97316", color: "#ffffff" },
  gray: { fillColor: "#6b7280", color: "#ffffff" },
  hidden: { fillColor: "transparent", color: "transparent" },
};

const KNOWN_TASK_LABELS: Record<string, string> = {
  "Zadanie koordynatora": "admin.taskNames.coordinator",
  "Zadanie długoterminowe": "admin.taskNames.longTerm",
  "Zadanie dodatkowe": "admin.taskNames.extra",
  "Zadanie standardowe": "admin.taskNames.standard",
};

function translateTaskLabel(name: string | null | undefined, t: TFunction, emptyKey: "admin.map.noTasks" | "admin.map.doneShort"): string {
  if (!name) return t(emptyKey);
  const key = KNOWN_TASK_LABELS[name];
  return key ? t(key) : name;
}

function statusLabel(status: MarkerStatus, mode: AdminMapMode, t: TFunction): string {
  switch (status) {
    case "red":
      return t("admin.map.overdue");
    case "yellow":
      return t("admin.map.inProgress");
    case "green":
      return mode === "snow" ? t("admin.map.snowed") : t("admin.map.done");
    case "orange":
      return t("admin.map.longTerm");
    case "hidden":
      return t("admin.map.futureOnly");
    default:
      return mode === "snow" ? t("admin.map.noSnowAfterCutoff") : t("admin.map.noTasksToday");
  }
}

function formatSnowCompletedAt(iso: string | null | undefined, emptyLabel: string, locale: string): string {
  if (!iso) return emptyLabel;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return emptyLabel;
  return d.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function MapFitBounds({ locations }: { locations: MapLocation[] }) {
  const map = useMap();
  const withCoords = useMemo(
    () => locations.filter((l) => l.latitude != null && l.longitude != null),
    [locations]
  );
  useEffect(() => {
    if (withCoords.length === 0) return;
    const bounds = L.latLngBounds(
      withCoords.map((l) => [l.latitude!, l.longitude!] as [number, number])
    );
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 14 });
  }, [map, withCoords]);
  return null;
}

export function AdminMap({
  locations,
  selectedLocationId,
  onMarkerClick,
  onClosePreview,
  onDetails,
  className,
  mode = "cleaning",
}: AdminMapProps) {
  const withCoords = useMemo(() => {
    const parseCoord = (v: unknown): number | null => {
      if (v == null) return null;
      const n = typeof v === "number" ? v : (typeof v === "string" ? parseFloat(v) : NaN);
      return Number.isNaN(n) ? null : n;
    };
    const valid: MapLocation[] = [];
    const withoutValidCoords: MapLocation[] = [];
    for (const l of locations) {
      const lat = parseCoord(l.latitude);
      const lng = parseCoord(l.longitude);
      if (lat == null || lng == null) {
        withoutValidCoords.push(l);
        continue;
      }
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        withoutValidCoords.push(l);
        continue;
      }
      valid.push({ ...l, latitude: lat, longitude: lng });
    }
    if (withoutValidCoords.length > 0) {
      console.warn(
        "[AdminMap] Pominięto znaczniki bez prawidłowych współrzędnych:",
        withoutValidCoords.map((l) => ({ id: l.id, address: l.address, lat: l.latitude, lng: l.longitude }))
      );
    }
    return valid;
  }, [locations]);

  const selectedLoc = useMemo(
    () => (selectedLocationId ? locations.find((l) => l.id === selectedLocationId) ?? null : null),
    [locations, selectedLocationId]
  );

  return (
    <FunctionalConsentGate>
    <div
      className={cn(
        "admin-map rounded-lg border border-border overflow-hidden bg-muted/30 relative z-0",
        className
      )}
    >
      <MapContainer
        center={defaultCenter}
        zoom={10}
        className="h-full w-full min-h-[320px] relative z-0"
        scrollWheelZoom
      >
        <TileLayer
          attribution={OSM_ATTRIBUTION}
          url={OSM_TILE_URL}
        />
        {withCoords.length > 0 && <MapFitBounds locations={withCoords} />}
        {withCoords.map((loc) => {
          const style = loc.status === "hidden" ? statusStyles.gray : statusStyles[loc.status];
          return (
            <CircleMarker
              key={loc.id}
              center={[loc.latitude!, loc.longitude!]}
              radius={MARKER_RADIUS}
              pathOptions={{
                fillColor: style.fillColor,
                color: style.color,
                weight: MARKER_WEIGHT,
                fillOpacity: 1,
              }}
              eventHandlers={{
                click: () => onMarkerClick?.(loc.id),
              }}
            />
          );
        })}
      </MapContainer>

      {selectedLoc && (
        <QuickViewPanel
          location={selectedLoc}
          mode={mode}
          onClose={onClosePreview}
          onDetails={onDetails}
        />
      )}
    </div>
    </FunctionalConsentGate>
  );
}

interface QuickViewPanelProps {
  location: MapLocation;
  mode: AdminMapMode;
  onClose?: () => void;
  onDetails?: (id: string) => void;
}

function QuickViewPanel({ location, mode, onClose, onDetails }: QuickViewPanelProps) {
  const { t, i18n } = useTranslation();
  console.log("[Map Marker Data]", {
    id: location.id,
    address: location.address,
    status: location.status,
    currentWorkerName: location.currentWorkerName,
    nextTaskName: location.nextTaskName,
    taskProgress: location.taskProgress,
    lastCompletedAt: location.lastCompletedAt,
    mode,
  });
  const statusText = statusLabel(location.status, mode, t);
  const statusVariant =
    location.status === "green"
      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
      : location.status === "yellow"
        ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
        : location.status === "red"
          ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
          : location.status === "orange"
            ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
            : location.status === "hidden"
              ? "bg-muted/50 text-muted-foreground"
              : "bg-muted text-muted-foreground";

  return (
    <div
      className="absolute bottom-4 left-4 right-4 z-[1000] max-w-md rounded-lg border border-border bg-card p-4 shadow-lg"
      role="dialog"
      aria-label={t("admin.map.quickPreview")}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-foreground truncate">{location.address}</h3>
            <span
              className={cn(
                "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                statusVariant
              )}
            >
              {statusText}
            </span>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="shrink-0 h-8 w-8"
          aria-label={t("admin.map.closePreview")}
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
        <div className="flex gap-2">
          <User className="size-4 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
          <div>
            <p className="text-muted-foreground">{t("admin.map.whoWorks")}</p>
            <p className="mt-0.5 font-medium">
              {location.currentWorkerName ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block size-2 rounded-full bg-green-500" aria-hidden />
                  {t("admin.map.loggedIn", { name: location.currentWorkerName })}
                </span>
              ) : (
                t("admin.map.noStaff")
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Clock className="size-4 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
          <div>
            <p className="text-muted-foreground">
              {mode === "snow" ? t("admin.map.lastSnow") : t("admin.map.nextTask")}
            </p>
            <p className="mt-0.5 font-medium">
              {mode === "snow" ? (
                formatSnowCompletedAt(
                  location.lastCompletedAt,
                  t("admin.map.noSnowAfterTime"),
                  intlLocaleFor(i18n.language)
                )
              ) : location.taskProgress && location.taskProgress.total > 0 ? (
                <>
                  {location.nextTaskName
                    ? translateTaskLabel(location.nextTaskName, t, "admin.map.noTasks")
                    : location.taskProgress.completed === location.taskProgress.total
                      ? t("admin.map.doneShort")
                      : t("admin.map.noTasks")}
                  <span className="ml-1 text-muted-foreground font-normal">
                    ({location.taskProgress.completed}/{location.taskProgress.total})
                  </span>
                </>
              ) : (
                translateTaskLabel(location.nextTaskName, t, "admin.map.noTasks")
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={() => onDetails?.(location.id)} className="gap-2">
          {t("admin.map.details")}
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}
